/**
 * Created: 2026-04-08
 * Initial commit: Split SlotsPage into SlotsPage/ directory (feat/event/mr-10)
 *
 * DataGridPro wrapper for the slot grid with cell rendering and styling.
 */

import { Box, CircularProgress, Tooltip } from '@mui/material'
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro'
import type { SlotMeta } from './types'
import { DRAG_HIGHLIGHT } from './useDragSelection'

export { DRAG_HIGHLIGHT }

interface SlotGridProps {
  rows: { id: number; hour: string }[]
  columns: GridColDef[]
  loading: boolean
  gridRef: React.RefObject<HTMLDivElement | null>
  onMouseDown: (e: React.MouseEvent) => void
  onMouseMove: (e: React.MouseEvent) => void
  onMouseUp: (e: React.MouseEvent) => void
  onCellClick: (params: { id: number | string; field: string }) => void
  getCellClassName: (params: { id: number | string; field: string }) => string
}

export default function SlotGrid({
  rows,
  columns,
  loading,
  gridRef,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onCellClick,
  getCellClassName,
}: SlotGridProps) {
  return (
    <Box
      ref={gridRef}
      sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <DataGridPro
        rows={rows}
        columns={columns}
        hideFooter
        disableRowSelectionOnClick
        disableColumnMenu
        density="compact"
        rowSpanning
        rowHeight={52}
        onCellClick={onCellClick}
        getCellClassName={getCellClassName}
        sx={{
          height: '100%',
          userSelect: 'none',
          '& .MuiDataGrid-cell': {
            borderRight: '1px solid rgba(224, 224, 224, 1)',
            cursor: 'cell',
            display: 'flex',
            alignItems: 'center',
            py: 1,
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: '1px solid rgba(224, 224, 224, 1)',
          },
          '& .unavailable-cell': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            color: 'rgba(0, 0, 0, 0.38)',
            cursor: 'default',
          },
          '& .past-cell': {
            backgroundColor: '#f5f5f5',
            color: 'rgba(0, 0, 0, 0.38)',
            cursor: 'default',
          },
          '& .status-reserved': {
            backgroundColor: '#fff3e0',
            color: '#e65100',
          },
          '& .status-booked': {
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
          },
          '& .status-locked': {
            backgroundColor: '#e0e0e0',
            color: '#424242',
          },
          '& .cell-selected': {
            outline: '2px solid #1976d2',
            outlineOffset: '-2px',
          },
          [`& .${DRAG_HIGHLIGHT}`]: {
            backgroundColor: 'rgba(25, 118, 210, 0.16) !important',
          },
          '& [role="presentation"][data-colindex]': {
            borderRight: '1px solid rgba(224, 224, 224, 1)',
          },
        }}
        initialState={{
          pinnedColumns: { left: ['hour'] },
        }}
      />
    </Box>
  )
}

/** Render the content of a slot cell (price + optional reason label) */
export function SlotCellContent({ meta }: { meta: SlotMeta }) {
  if (meta.reason) {
    return (
      <Tooltip
        title={meta.reason}
        arrow
        placement="top"
        slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ lineHeight: 'normal' }}>¥{meta.price}</Box>
          <Box
            sx={{
              fontSize: '0.7rem',
              color: '#e65100',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 'normal',
            }}
          >
            {meta.reason.split('\n')[0]}
          </Box>
        </Box>
      </Tooltip>
    )
  }
  return (
    <Box sx={{ width: '100%', textAlign: 'center' }}>
      <Box>¥{meta.price}</Box>
    </Box>
  )
}
